<template>
  <div class="chart">
    <h3>{{ $t('statsChart.title') }}</h3>

    <ul class="legend">
      <li>{{ $t('statsChart.all') }}</li>
      <li>{{ $t('statsChart.confirmed') }}</li>
      <li>{{ $t('statsChart.rejected') }}</li>
    </ul>

    <line-chart
      v-if="showChart"
      :chart-data="dataCollection"
      :options="options"
      :height="250"
      :width="600">
    </line-chart>
  </div>
</template>

<script>
import ErrorUtils from './../helpers/ErrorUtils.js';
import LineChart from './LineChart';

export default {
  name: 'stats-chart',
  components: {
    'line-chart': LineChart
  },
  data () {
    return {
      dateStart: false,
      dateEnd: false,
      dataCollection: null,
      options: {
        responsive: true,
        maintainAspectRatio: true,
        legend: {
          display: false
        },
        scales: {}
      },
      showChart: false
    }
  },
  mounted () {
    this.$bus.$on('dashboard-stats-date-range-changed', this.statsDateRangeChanged);

    setTimeout(() => {
      if (!this.dateStart) {
        this.dateStart = this.$parent.dateStart;
        this.dateEnd = this.$parent.dateEnd;
      }

      this.fillData();
    }, 500);
  },
  methods: {
    statsDateRangeChanged (range) {
      this.dateStart = [
        range.startDate.getFullYear(),
        range.startDate.getMonth() + 1,
        range.startDate.getDate()
      ].map(n => n < 10 ? '0' + n : n).join('-');
      this.dateEnd = [
        range.endDate.getFullYear(),
        range.endDate.getMonth() + 1,
        range.endDate.getDate()
      ].map(n => n < 10 ? '0' + n : n).join('-');
      this.showChart = false;
      this.fillData();
    },
    setData (start, end) {
      this.dateStart = start;
      this.dateEnd = end;
      this.fillData();
    },
    fillData () {
      this.$http.get('/panel/stats/stats-dashboard-inquiries-status', {
        params: {
          'date_from': this.dateStart,
          'date_to': this.dateEnd
        }
      }).then(response => {
        let data = response.data;
        let labels = [];
        let all = [];
        let accepted = [];
        let rejected = [];
        let maxValue = 0;

        for (let item of data) {
          labels.push(item.date);
          all.push(item.all);
          accepted.push(item.accepted);
          rejected.push(item.rejected);
          maxValue = Math.max(maxValue, item.all, item.accepted, item.rejected);
        }

        maxValue += 1;

        this.options.scales.yAxes = [{
          ticks: {
            beginAtZero: true,
            fontColor: '#aaa',
            fontSize: 10,
            suggestedMax: maxValue,
            callback: function (value, index, values) {
              if (Math.floor(value) === value) {
                return value;
              }
            }
          }
        }];

        this.options.scales.xAxes = [{
          ticks: {
            maxRotation: 0,
            fontColor: '#aaa',
            fontSize: 10,
            callback: function (value, index, values) {
              return value.split('-').pop();
            }
          },
          gridLines: {
            display: false
          }
        }];

        this.options.tooltips = {
          position: 'nearest',
          mode: 'index',
          intersect: false,
          bodySpacing: 10,
          xPadding: 12,
          yPadding: 12,
          caretPadding: 10,
          displayColors: false,
          callbacks: {
            title: function () {
              return '';
            },
            beforeLabel: function (tooltipItem, data) {
              if (tooltipItem.datasetIndex === 0) {
                return data.labels[tooltipItem.index];
              }

              return '';
            }
          }
        };

        this.dataCollection = {
          labels: labels,
          datasets: [
            {
              label: this.$t('statsChart.rejected'),
              showLine: true,
              borderColor: '#ED1C23',
              backgroundColor: 'rgba(38, 150, 233, .125)',
              pointRadius: 0,
              lineTension: 0,
              borderCapStyle: 'round',
              borderJoinStyle: 'round',
              data: rejected
            },
            {
              label: this.$t('statsChart.accepted'),
              showLine: true,
              borderColor: '#69B228',
              backgroundColor: 'rgba(38, 150, 233, .125)',
              pointRadius: 0,
              lineTension: 0,
              borderCapStyle: 'round',
              borderJoinStyle: 'round',
              data: accepted
            },
            {
              label: this.$t('statsChart.all'),
              showLine: true,
              borderColor: '#2696E9',
              backgroundColor: 'rgba(38, 150, 233, .125)',
              pointRadius: 0,
              lineTension: 0,
              borderCapStyle: 'round',
              borderJoinStyle: 'round',
              data: all
            }
          ]
        };

        this.showChart = true;
      }).catch(error => {
        ErrorUtils.handleAjaxError(error, '/panel/stats/stats-dashboard-inquiries-status', this.$router);
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('dashboard-stats-date-range-changed', this.statsDateRangeChanged);
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.chart {
  border: 1px solid $color-border;
  border-radius: $border-radius-small;
  padding: 3rem;
  position: relative;
  width: 100%;

  h3 {
    font-size: 2.4rem;
    font-weight: 400;
    line-height: 1;
    margin: 0;
  }

  canvas {
    max-width: 100%;
  }

  .legend {
    display: flex;
    list-style-type: none;
    margin: 2rem 0;
    padding: 0;

    li {
      color: $color-text-light;
      font-size: 1.2rem;
      font-weight: 400;
      margin-left: 2rem;
      margin-right: 3rem;
      position: relative;
      text-transform: uppercase;

      &:before {
        border-radius: $border-radius-full;
        content: "";
        height: 1.2rem;
        left: -2rem;
        position: absolute;
        top: 0;
        width: 1.2rem;
      }

      &:nth-child(1):before {
        background: $color-primary;
      }

      &:nth-child(2):before {
        background: $color-success;
      }

      &:nth-child(3):before {
        background: $color-danger;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/StatsChart.vue